import { inject, Injectable } from '@angular/core';
import {
  Receipt,
  ReceiptCsvEntry,
  ReceiptTableEntry,
  ReceiptType,
} from '@pay/receipts/data/receipts.model';
import { UserDecimalPipe } from '@shared/pipes/user-decimal.pipe';
import { LinkCellData } from '@widgets/innogy-data-table/link-cell/link-cell.component';
import { CellType } from '@widgets/innogy-data-table/dynamic-cell/dynamic-cell.component';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class ReceiptMapperService {
  private readonly userDecimalPipe: UserDecimalPipe = inject(UserDecimalPipe);
  private readonly translate: TranslateService = inject(TranslateService);

  public mapToTableData(receipts: Receipt[]): ReceiptTableEntry[] {
    return this.mapToCsvOrTableData(receipts, true) as ReceiptTableEntry[];
  }

  public mapToCsvData(receipts: Receipt[], receiptType: ReceiptType): ReceiptCsvEntry[] {
    return this.mapToCsvOrTableData(receipts, false, receiptType) as ReceiptCsvEntry[];
  }

  private mapToCsvOrTableData(
    receipts: Receipt[],
    isTableData = false,
    receiptType?: ReceiptType
  ): ReceiptTableEntry[] | ReceiptCsvEntry[] {
    if (isTableData) {
      return receipts.map((receipt, index) => {
        return {
          id: receipt.id,
          startTimestamp: receipt.sessionStart,
          terminalId: receipt.terminalId,
          transactionId: receipt.transactionId,
          cardNumberSuffix: receipt.cardNumber,
          cardType: receipt.cardType,
          totalAmount: this.userDecimalPipe.transform(
            receipt.totalPrice,
            '1.2-2',
            receipt.currency,
            'narrow'
          ),
          chargepointId: this.getChargePortIdCell(receipt.stationId, receipt.chargePortName),
          email: receipt.maskedEmail ?? '-',
          createdAt: receipt.created,
          download: {
            cellType: CellType.BUTTON,
            buttonConfig: {
              text: this.translate.instant('GLOBAL.DOWNLOAD'),
              icon: 'document-download-outlined',
              testId: `download-receipt-${index}`,
            },
          },
          downloadId: receipt.downloadId,
        } as ReceiptTableEntry;
      });
    } else {
      return receipts.map(receipt => {
        return {
          startTimestamp: receipt.sessionStart,
          ...(receiptType === ReceiptType.PAYMENT_TERMINAL && { terminalId: receipt.terminalId }),
          transactionId: receipt.transactionId,
          cardNumberSuffix: receipt.cardNumber,
          cardType: receipt.cardType,
          totalAmount: this.userDecimalPipe.transform(
            receipt.totalPrice,
            '1.2-2',
            receipt.currency,
            'narrow'
          ),
          ...(receiptType === ReceiptType.DIRECT_PAYMENT && { email: receipt.maskedEmail ?? '-' }),
          chargepointId: receipt.chargePortName,
          createdAt: receipt.created,
        } as ReceiptCsvEntry;
      });
    }
  }

  private getChargePortIdCell(stationUuid: string, chargePortId: string): LinkCellData {
    return {
      text: chargePortId,
      url:
        '/operate/chargingInfrastructureDetail?uuid=' + stationUuid + '&chargePort=' + chargePortId,
      showTooltip: true,
    };
  }
}
